import { cn } from '@/lib/utils';

export type QueueTab = 'open' | 'history';

export interface OpenHistoryTabsProps {
  value: QueueTab;
  onChange: (v: QueueTab) => void;
  className?: string;
}

const TABS: { value: QueueTab; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'history', label: 'History' },
];

/**
 * Pill-style two-tab control for switching between the open queue and
 * resolved history. Controlled — the page owns the `value` state and is
 * expected to mirror it into `?tab=` if it wants URL persistence.
 *
 * No shared `Tabs` primitive exists in the component library yet, so this
 * stands on its own. When one lands later we'll swap the internals without
 * touching call sites.
 */
export function OpenHistoryTabs({ value, onChange, className }: OpenHistoryTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="Queue view"
      className={cn(
        'inline-flex items-center gap-4 rounded-full border border-neutral-200 bg-neutral-100 p-4',
        className,
      )}
    >
      {TABS.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            className={cn(
              'h-9 rounded-full px-16 text-sm font-medium transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
              active
                ? 'bg-neutral-0 text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900',
            )}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
